import { useTranslation } from 'react-i18next';

import { ComponentType } from 'react';

import {
  IconBuildings,
  IconChartBar,
  IconChecklist,
  IconClipboardList,
  IconClock,
  IconListCheck,
  IconMatrix,
  IconSettings,
  IconTemperature,
  IconUsers,
} from '@tabler/icons-react';

import { useRouter, useRouterState } from '@tanstack/react-router';

import { AppSidebarNavItem, AppSidebarSection, AppSidebarView } from '@optiro/ui';

import { CompanyLogo } from '@/components/CompanyLogo/CompanyLogo';
import useProjects from '@/hooks/projects/useProjects';
import {
  DASHBOARD_VARIANT_LABELS,
  getAllowedDashboardVariants,
} from '@/modules/Dashboard/useDashboardVariant';
import { useAuth } from '@/providers/AuthProvider/AuthProvider';
import type { FileRoutesByTo } from '@/routeTree.gen';
import type { Account } from '@/types/types';
import { getStatusColor } from '@/utils/projectStatus';

import logo from './optiro.svg';

type AllowedRole = Account['role'];

interface NavItemConfig {
  key: string;
  text: string;
  icon: ComponentType<{ size?: number; stroke?: number; color?: string }>;
  href: keyof FileRoutesByTo;
  role?: AllowedRole | AllowedRole[];
}

interface SectionConfig {
  key: string;
  role: AllowedRole | AllowedRole[];
  items: NavItemConfig[];
}

/**
 * No text headlines — order is the hierarchy: daily workflow first, then
 * planning tools, with directories (Medarbejdere/Partnere) and admin-only
 * pages pushed into the footer cluster since they're occasional lookups,
 * not daily tasks.
 */
const NAV_ITEMS: SectionConfig[] = [
  {
    key: 'daglig-brug',
    role: ['planner', 'administrator', 'employee'],
    items: [
      { key: 'dashboard', text: 'Overblik', icon: IconListCheck, href: '/app/dashboard' },
      {
        key: 'activities',
        text: 'Mine aktiviteter',
        icon: IconClipboardList,
        href: '/app/activities',
      },
      {
        key: 'projects',
        text: 'Projekter',
        icon: IconChecklist,
        href: '/app/projects',
        role: ['planner', 'administrator'],
      },
      { key: 'timesheet', text: 'Tidsregistrering', icon: IconClock, href: '/app/timesheet' },
    ],
  },
  {
    key: 'planlaegning',
    role: ['planner', 'administrator'],
    items: [
      { key: 'planning', text: 'Planlægning', icon: IconMatrix, href: '/app/planning' },
      { key: 'heatmap', text: 'Heatmap', icon: IconTemperature, href: '/app/planning/resources' },
    ],
  },
];

const FOOTER_ITEMS: (NavItemConfig & {
  translationKey?: string;
  role: AllowedRole | AllowedRole[];
})[] = [
  {
    key: 'resources',
    text: 'Medarbejdere',
    icon: IconUsers,
    href: '/app/resources',
    role: ['planner', 'administrator'],
  },
  {
    key: 'partners',
    text: 'Partnere',
    icon: IconBuildings,
    href: '/app/partners',
    role: ['planner', 'administrator'],
  },
  {
    key: 'reports',
    text: 'Reports',
    translationKey: 'reports:nav_label',
    icon: IconChartBar,
    href: '/app/reports',
    role: 'administrator',
  },
  {
    key: 'settings',
    text: 'Administration',
    icon: IconSettings,
    href: '/app/settings',
    role: 'administrator',
  },
];

function hasRole(userRole: AllowedRole | undefined, role: AllowedRole | AllowedRole[]): boolean {
  if (!userRole) return false;
  return Array.isArray(role) ? role.includes(userRole) : userRole === role;
}

const SideBar = ({ opened, handleToggle }: { opened: boolean; handleToggle: () => void }) => {
  const { location } = useRouterState();
  const { userData } = useAuth();
  const router = useRouter();
  const { t } = useTranslation();

  const userRole = userData?.role;
  const canSeeProjects = hasRole(userRole, ['planner', 'administrator']);

  const { data: myProjects, isLoading: myProjectsLoading } = useProjects({
    status: ['draft', 'planning', 'active'],
    manager_ids: userData ? [userData.id] : [],
  });

  const allowedDashboardVariants = getAllowedDashboardVariants(userRole, userData?.company.status);

  const navigate = (href: string) => {
    handleToggle();
    router.navigate({ to: href });
  };

  const sections: AppSidebarSection[] = NAV_ITEMS.filter((section) =>
    hasRole(userRole, section.role)
  ).map((section) => ({
    key: section.key,
    items: section.items
      .filter((item) => !item.role || hasRole(userRole, item.role))
      .map((item): AppSidebarNavItem => {
        if (item.key === 'dashboard') {
          return {
            key: item.key,
            label: item.text,
            icon: item.icon,
            href: item.href,
            active: location.pathname.startsWith('/app/dashboard'),
            subList:
              allowedDashboardVariants.length > 1
                ? {
                    items: allowedDashboardVariants.map((v) => {
                      const href = `/app/dashboard/${v}`;
                      return {
                        key: v,
                        label: DASHBOARD_VARIANT_LABELS[v],
                        href,
                        active: location.pathname === href,
                      };
                    }),
                  }
                : undefined,
          };
        }

        return {
          key: item.key,
          label: item.text,
          icon: item.icon,
          href: item.href,
          active: location.pathname === item.href,
          subList:
            item.key === 'projects' && canSeeProjects
              ? {
                  loading: myProjectsLoading,
                  items: (myProjects ?? []).map((project) => {
                    const href = `/app/projects/${project.id}`;
                    return {
                      key: String(project.id),
                      label: project.name,
                      href,
                      active: location.pathname === href,
                      color: getStatusColor(project.status),
                    };
                  }),
                  emptyLabel: 'Ingen aktive projekter',
                  quickAddLabel: 'Nyt projekt',
                  onQuickAdd: () => {
                    handleToggle();
                    const button = document.querySelector<HTMLButtonElement>(
                      '[data-testid="create-project-button"]'
                    );
                    button?.click();
                  },
                }
              : undefined,
        };
      }),
  }));

  const footerItems: AppSidebarNavItem[] = FOOTER_ITEMS.filter((item) =>
    hasRole(userRole, item.role)
  ).map((item) => ({
    key: item.key,
    label: item.translationKey ? t(item.translationKey) : item.text,
    icon: item.icon,
    href: item.href,
    active: location.pathname === item.href,
  }));

  const logoSrc = userData?.company.logo?.url && !userData.company.logo.url.includes('fallback');

  return (
    <AppSidebarView
      logo={
        logoSrc ? (
          <CompanyLogo
            logoUrl={userData!.company.logo!.url}
            name={userData!.company.name}
            size={30}
            onClick={() => navigate('/app/dashboard')}
            style={{ cursor: 'pointer' }}
          />
        ) : (
          <img
            alt="Logo"
            src={logo}
            height={30}
            style={{ maxWidth: 100, objectFit: 'contain', cursor: 'pointer' }}
            onClick={() => navigate('/app/dashboard')}
          />
        )
      }
      sections={sections}
      footerItems={footerItems}
      onNavigate={navigate}
      opened={opened}
      onClose={handleToggle}
    />
  );
};

export default SideBar;
